import { Delete as DeleteIcon, Edit as EditIcon } from "@mui/icons-material";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";

import Price from "../../common/Price";
import { TaxAndFeeSetting } from "../../../types/admin";

interface TaxesFeesTableProps {
  settings: TaxAndFeeSetting[];
  loading?: boolean;
  onEdit: (setting: TaxAndFeeSetting) => void;
  onDelete: (setting: TaxAndFeeSetting) => void;
}

const formatPercent = (value: number | null) =>
  value !== null && value !== undefined ? `${value}%` : "—";
const TaxesFeesTable: React.FC<TaxesFeesTableProps> = ({
  settings,
  loading,
  onEdit,
  onDelete,
}) => {
  const { t } = useTranslation();
  return (
    <Paper>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t("admin.taxesFees.tax")}</TableCell>
              <TableCell>{t("admin.taxesFees.fees")}</TableCell>
              <TableCell>{t("admin.taxesFees.minAmount")}</TableCell>
              <TableCell>{t("admin.taxesFees.maxAmount")}</TableCell>
              <TableCell>{t("admin.taxesFees.status")}</TableCell>
              <TableCell>{t("admin.taxesFees.comment")}</TableCell>
              <TableCell>{t("admin.taxesFees.created")}</TableCell>
              <TableCell>{t("admin.taxesFees.updated")}</TableCell>
              <TableCell align="right">{t("admin.taxesFees.actions")}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Box display="flex" justifyContent="center" py={3}>
                    <CircularProgress size={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : settings.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary">
                    {t("admin.taxesFees.noConfigurationsFound")}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              settings.map((setting) => (
                <TableRow key={setting.id} hover>
                  <TableCell>{formatPercent(setting.tax)}</TableCell>
                  <TableCell>{formatPercent(setting.fees)}</TableCell>
                  <TableCell>
                    <Price value={setting.min} disableCentsHeuristic />
                  </TableCell>
                  <TableCell>
                    <Price value={setting.max} disableCentsHeuristic />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={setting.isActive ? t("admin.taxesFees.active") : t("admin.taxesFees.inactive")}
                      color={setting.isActive ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {setting.comment ? (
                      <Tooltip title={setting.comment}>
                        <Typography
                          variant="body2"
                          sx={{
                            maxWidth: 200,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {setting.comment}
                        </Typography>
                      </Tooltip>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                  <TableCell>
                    {new Date(setting.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell>
                    {setting.updatedAt
                      ? new Date(setting.updatedAt).toLocaleString()
                      : "—"}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      color="primary"
                      size="small"
                      onClick={() => onEdit(setting)}
                      aria-label={t("admin.taxesFees.editAriaLabel")}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      color="error"
                      size="small"
                      onClick={() => onDelete(setting)}
                      aria-label={t("admin.taxesFees.deleteAriaLabel")}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default TaxesFeesTable;
